import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, growthServiceTopic, liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { GrowthPartyLabelService } from './growth-party-label.service';
import { GrowthServiceService, GrowthSuccessPlan } from './growth-service.service';

@Component({
  standalone: true,
  selector: 'ain-growth-success-plans',
  imports: [AinPageComponent, InfoHintComponent, TableModule, FormsModule, ButtonModule, InputTextModule],
  template: `
    <ain-page [title]="t('growth.service.successPlans.title')" [description]="t('growth.service.successPlans.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.service.successPlans.help.title'"
        [descriptionKey]="'growth.service.successPlans.help.description'"
        [bulletKeys]="['growth.service.successPlans.help.bullets.health', 'growth.service.successPlans.help.bullets.live']" />

      <section class="card list-card">
        <div class="toolbar">
          <input
            pInputText
            type="text"
            [(ngModel)]="searchName"
            [placeholder]="t('growth.service.successPlans.searchName')" />
          <button pButton type="button" [label]="t('growth.service.successPlans.search')" (click)="applySearch()"></button>
          <button pButton type="button" [label]="t('growth.service.successPlans.refresh')" severity="secondary" (click)="load()"></button>
          <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
        </div>
        <div class="toolbar">
          <button pButton type="button" [label]="t('growth.service.successPlans.addNew')" (click)="openCreate()"></button>
          <button
            pButton
            type="button"
            [label]="t('growth.service.successPlans.editSelected')"
            severity="secondary"
            [disabled]="!selected()"
            (click)="openEditSelected()"></button>
        </div>
        <p-table
          [value]="filteredRows()"
          selectionMode="single"
          [selection]="selected()"
          (selectionChange)="onSelect($event)"
          dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('growth.service.successPlans.columns.name') }}</th>
              <th>{{ t('growth.service.successPlans.columns.status') }}</th>
              <th>{{ t('growth.service.successPlans.columns.healthScore') }}</th>
              <th>{{ t('growth.service.successPlans.columns.partyId') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [pSelectableRow]="row">
              <td>
                <button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.name }}</button>
              </td>
              <td>{{ row.status }}</td>
              <td>{{ row.healthScore ?? '-' }}</td>
              <td>{{ partyLabels.label(row.partyId) }}</td>
            </tr>
          </ng-template>
        </p-table>
        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      </section>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; }
    .list-card { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .link-btn:hover { text-decoration: underline; }
    .message { margin: 0; font-size: 13px; }
    .message.error { color: #b91c1c; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
  `]
})
export class GrowthSuccessPlansComponent implements OnInit {
  private readonly api = inject(GrowthServiceService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly partyLabels = inject(GrowthPartyLabelService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly rows = signal<GrowthSuccessPlan[]>([]);
  readonly filteredRows = signal<GrowthSuccessPlan[]>([]);
  readonly selected = signal<GrowthSuccessPlan | null>(null);
  readonly errorMessage = signal('');

  searchName = '';

  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthServiceChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.load();
  }

  load(): void {
    this.errorMessage.set('');
    this.api.listSuccessPlans().subscribe({
      next: data => {
        this.partyLabels.resolve(data.map(item => item.partyId));
        this.rows.set(data);
        this.applySearch();
      },
      error: () => {
        this.rows.set([]);
        this.filteredRows.set([]);
        this.selected.set(null);
        this.errorMessage.set(this.t('growth.service.successPlans.messages.loadFailed'));
      }
    });
  }

  applySearch(): void {
    const term = this.searchName.trim().toLowerCase();
    const filtered = !term
      ? this.rows()
      : this.rows().filter(item => item.name.toLowerCase().includes(term));
    this.filteredRows.set(filtered);
    this.selected.set(filtered[0] ?? null);
  }

  onSelect(row: GrowthSuccessPlan | null): void {
    this.selected.set(row);
  }

  openCreate(): void {
    void this.router.navigate(['/growth/service/success-plans/new']);
  }

  openEditSelected(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.openDetail(row.id);
  }

  openDetail(id: string): void {
    void this.router.navigate(['/growth/service/success-plans', id]);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthServiceTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
